import React, { useMemo } from 'react'

import PropTypes from 'prop-types'
import { prop } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import { Typography } from '@mui/material'

import { haveAlerts, haveAll, haveTargets } from '~/src/DataType/TargetRange/utils'
import createLogger from '~/src/Lib/Logging'
import { datePropType, idAndNameShape } from '~/src/Lib/PropTypes'
import { isNumber } from '~/src/Lib/Utils'
import DataTypeName from '~/src/UI/Shared/DataTypeName'

import Base from '../Base'
import { dateFormatType } from '../utils'
import {
  getAlerts,
  getAll,
  getTargets,
  getUnits,
} from './utils'

const logger = createLogger('TargetRange/Formatter')

const TargetRangeFormatter = ({
  annotation,
  growlog = false,
  harvest = null,
  phase = null,
  shortForm = false,
  ...rest
}) => {
  const { dataTypeKey } = annotation
  const { dataTypes } = useConnect('selectDataTypes')
  const dataType = dataTypes?.[dataTypeKey]
  const getterProps = useMemo(() => ({ annotation }), [annotation])
  const RangeContent = haveAll(annotation) ? getAll(getterProps, dataType) : (
    <span>
      {haveTargets(annotation) ? getTargets(getterProps, dataType) : null}
      {haveAlerts(annotation) ? getAlerts(getterProps, dataType) : null}
      &nbsp;
      {shortForm ? getUnits(dataType) : null}
      {isNumber(annotation?.depth) && ` at ${annotation.depth}"`}
    </span>
  )
  const HarvestContent = phase || harvest
    ? [growlog ? null : harvest, phase].map(prop('name')).filter(Boolean).join(' - ')
    : null

  return (
    <Base
      {...rest}
      title={<Typography variant="body1"><DataTypeName dataType={dataType} /></Typography>}
      body={HarvestContent ? (
        <div style={growlog ? { display: 'flex', width: '100%' } : undefined}>
          <div style={growlog ? { flex: 1 } : undefined}>{RangeContent}</div>
          <div style={growlog ? { flex: 1 } : undefined}>{HarvestContent}</div>
        </div>
      ) : RangeContent}
      date={annotation.startDate ? [annotation.startDate, annotation.endDate].filter(Boolean) : null}
    />
  )
}
TargetRangeFormatter.propTypes = {
  annotation: PropTypes.shape({
    dataTypeKey: PropTypes.string.isRequired,
    depth: PropTypes.number,
    targetMin: PropTypes.number,
    targetMax: PropTypes.number,
    alertMin: PropTypes.number,
    alertMax: PropTypes.number,
    startDate: datePropType,
    endDate: datePropType,
  }).isRequired,
  dateFormat: dateFormatType,
  growlog: PropTypes.bool,
  harvest: idAndNameShape,
  phase: idAndNameShape,
  shortForm: PropTypes.oneOf([true, false, 'all']),
}

TargetRangeFormatter.displayName = 'Annotation/TargetRange'

const Wrapped = React.memo(props => {
  const { phases, harvests } = useConnect('selectPhases', 'selectHarvests')
  if (props.growlog) {
    return <TargetRangeFormatter {...props} />
  }

  const { annotation: { phaseId } } = props
  const { [phaseId]: phase } = phases

  return (
    <TargetRangeFormatter
      {...props}
      phase={phaseId ? phase : null}
      harvest={phase ? harvests[phase.harvestId] : null}
    />
  )
})

export default Wrapped
